import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import type { UsageBucketItem } from "@/lib/api/client";

dayjs.extend(utc);

/** One chart row after gap-filling omitted API buckets. */
export type DailyUsageChartRow = {
  date: string;
  requests: number;
  errors: number;
};

const USAGE_RANGE_DAYS = 30;

/** UTC range for the last 30 calendar days (inclusive today); ``to`` is exclusive. */
export function getLast30DayRange(): { fromISO: string; toISO: string } {
  const todayStart = dayjs.utc().startOf("day");
  const fromStart = todayStart.subtract(USAGE_RANGE_DAYS - 1, "day");
  const toExclusive = todayStart.add(1, "day");
  return {
    fromISO: fromStart.toISOString(),
    toISO: toExclusive.toISOString(),
  };
}

/** Build a dense daily series for charting; missing buckets become zero. */
export function fillDailyBuckets(
  items: UsageBucketItem[],
  fromISO: string,
  toISO: string,
): DailyUsageChartRow[] {
  const byDate = new Map<string, { requests: number; errors: number }>();

  for (const item of items) {
    const dateKey = dayjs.utc(item.bucket).format("YYYY-MM-DD");
    byDate.set(dateKey, { requests: item.count, errors: item.error_count });
  }

  const rows: DailyUsageChartRow[] = [];
  let cursor = dayjs.utc(fromISO).startOf("day");
  const end = dayjs.utc(toISO).startOf("day");

  while (cursor.isBefore(end)) {
    const dateKey = cursor.format("YYYY-MM-DD");
    const bucket = byDate.get(dateKey);
    rows.push({
      date: dateKey,
      requests: bucket?.requests ?? 0,
      errors: bucket?.errors ?? 0,
    });
    cursor = cursor.add(1, "day");
  }

  return rows;
}
